import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {productionService} from '../../services/api';
import {toast} from 'react-toastify';

const ProductionBatchDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [stepAction, setStepAction] = useState({
                                                     step: null,
                                                     quantity: 0,
                                                     reason: '',
                                                     open: false
                                                 });
    const [confirmDialog, setConfirmDialog] = useState({
                                                           open: false,
                                                           action: null,
                                                           message: '',
                                                           title: ''
                                                       });

    useEffect(() => {
        const loadBatchDetails = async () => {
            try {
                setLoading(true);
                const data = await productionService.getProductionBatch(id);
                setBatch(data);
            }
            catch (error) {
                console.error("Error fetching production batch:", error);
                // Alert or toast notification could go here
            }
            finally {
                setLoading(false);
            }
        };

        loadBatchDetails();
    }, [id]);

    useEffect(() => {
        if (isDropdownOpen) {
            const handleClickOutside = (event) => {
                if (!event.target.closest('.dropdown-container')) {
                    setIsDropdownOpen(false);
                }
            };
            document.addEventListener('click', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [isDropdownOpen]);

    const handleStatusChange = async (newStatus) => {
        try {
            await productionService.updateProductionBatch(id, { status: newStatus });
            setBatch(prev => ({ ...prev, status: newStatus }));
            toast.success("Status updated successfully");
        }
        catch (error) {
            console.error("Error updating status:", error);
            toast.error("Error updating status: " + error.message);

        }
    };

    const handleAllocateMaterials = async () => {
        try {
            setConfirmDialog({ open: false, action: null, message: '', title: '' });
            const result = await productionService.allocateMaterials(id);

            // Update batch with new information
            setBatch(prev => ({
                ...prev,
                allocatedMaterials: result.allocatedMaterials,
                status: 'in_progress'
            }));

            toast.success("Allocated materials successfully");
        }
        catch (error) {
            console.error("Error allocating materials:", error);
            toast.error("Error allocating materials: " + error.message);

        }
    };

    const handleCompleteStep = async () => {
        if (!stepAction.step || stepAction.quantity <= 0) return;

        try {
            setStepAction(prev => ({ ...prev, open: false }));

            const result = await productionService.completeItem(id, {
                stepId: stepAction.step.id,
                quantity: stepAction.quantity
            });

            // Update batch with new completion information
            setBatch(prev => ({
                ...prev,
                completedQuantity: result.completedQuantity,
                productionSteps: prev.productionSteps.map(step =>
                                                              step.id === stepAction.step.id
                                                              ? { ...step, completedQuantity: step.completedQuantity + stepAction.quantity }
                                                              : step
                )
            }));

            toast.success("Step completed successfully");
        }
        catch (error) {
            console.error("Error completing step:", error);
            toast.error("Error completing step: " + error.message);
        }
    };

    const renderStatusBadge = (status) => {
        const classes = {
            'planned': 'bg-gray-100 text-gray-800',
            'in_progress': 'bg-yellow-100 text-yellow-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${classes[status] || 'bg-gray-100 text-gray-800'}`}>
                {status.replace('_', ' ')}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!batch) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Production Batch Not Found</h2>
                    <p className="text-gray-600 mb-6">The production batch you're looking for does not exist or has been removed.</p>
                    <button
                        onClick={() => navigate('/production')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
                    >
                        Return to Production List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-wrap justify-between items-center">
                    <div>
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate('/production')}
                                className="mr-4 text-gray-500 hover:text-gray-700"
                            >
                                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                                </svg>
                            </button>
                            <h1 className="text-2xl font-bold text-gray-800">{batch.batch_number}</h1>
                        </div>
                        <p className="mt-1 text-gray-600">
                            {batch.items && batch.items.length > 0
                             ? batch.items.map((item) => (
                                    <p key={item.product.sku}>{item.product.sku}{item.variant == 'carbon' ? '-C':'-S'} ({item.quantity})</p>
                                ))
                             : 'No items in batch'}
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                        {renderStatusBadge(batch.status)}
                        <button
                            onClick={() => navigate(`/production/${batch.id}/edit`)}
                            className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
                        >
                            Edit
                        </button>
                        <div className="relative inline-block text-left dropdown-container">
                            <button
                                type="button"
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none"
                                onClick={() => {
                                    setIsDropdownOpen(!isDropdownOpen)
                                }}
                            >
                                Actions
                            </button>
                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                                    <div className="py-1" role="menu" aria-orientation="vertical">
                                        {batch.status === 'planned' && (
                                            <button
                                                onClick={() => {
                                                    handleStatusChange('in_progress');
                                                    setIsDropdownOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                role="menuitem"
                                            >
                                                Start Production
                                            </button>
                                        )}
                                        {batch.status === 'in_progress' && (
                                            <button
                                                onClick={() => {
                                                    handleStatusChange('completed');
                                                    setIsDropdownOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                role="menuitem"
                                            >
                                                Mark as Completed
                                            </button>
                                        )}
                                        {(batch.status === 'planned' || batch.status === 'in_progress') && (
                                            <button
                                                onClick={() => {
                                                    handleStatusChange('cancelled');
                                                    setIsDropdownOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                role="menuitem"
                                            >
                                                Cancel Production
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'overview'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'items'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('items')}
                    >
                        Batch Items
                    </button>
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'materials'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('materials')}
                    >
                        Materials
                    </button>
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'issues'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('issues')}
                    >
                        Issues & Defects
                    </button>
                </nav>
            </div>

            {/* Content based on active tab */}
            <div className="bg-white rounded-lg shadow mb-6">
                {activeTab === 'overview' && (
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Batch Number</h3>
                                <p className="mt-1 text-lg text-gray-900">{batch.batch_number}</p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Status</h3>
                                <p className="mt-1">{renderStatusBadge(batch.status)}</p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                                <p className="mt-1 text-lg text-gray-900">{format(new Date(batch.created_at), 'MMMM d, yyyy')}</p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Planned Completion Date</h3>
                                <p className="mt-1 text-lg text-gray-900">
                                    {batch.planned_completion_date ?
                                     format(new Date(batch.planned_completion_date), 'MMMM d, yyyy') :
                                     'Not set'}
                                </p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Actual Completion Date</h3>
                                <p className="mt-1 text-lg text-gray-900">
                                    {batch.actual_completion_date ?
                                     format(new Date(batch.actual_completion_date), 'MMMM d, yyyy') :
                                     'Not completed yet'}
                                </p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Created By</h3>
                                <p className="mt-1 text-lg text-gray-900">{batch.user.name || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="mt-6 border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-medium text-gray-900">Production Progress</h3>
                            <div className="mt-4">
                                {batch.items && batch.items.length > 0 ? (
                                    <>
                                        <div className="flex justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">
                                                {batch.items.filter(item => item.status === 'completed').length} of {batch.items.length} items completed
                                            </span>
                                            <span className="text-sm font-medium text-gray-700">
                                                {Math.floor((batch.items.filter(item => item.status === 'completed').length / batch.items.length) * 100)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className="bg-blue-600 h-2.5 rounded-full"
                                                style={{ width: `${Math.min(100, Math.floor((batch.items.filter(item => item.status === 'completed').length / batch.items.length) * 100))}%` }}
                                            ></div>
                                        </div>
                                    </>
                                ) : (
                                     <p className="text-gray-500">No items in this batch</p>
                                 )}
                            </div>
                        </div>

                        {batch.notes && (
                            <div className="mt-6 border-t border-gray-200 pt-6">
                                <h3 className="text-lg font-medium text-gray-900">Notes</h3>
                                <p className="mt-2 text-gray-700">{batch.notes}</p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="mt-6 border-t border-gray-200 pt-6 flex justify-end space-x-3">
                            {batch.status === 'planned' && (
                                <button
                                    onClick={() => setConfirmDialog({
                                                                        open: true,
                                                                        action: () => handleStatusChange('in_progress'),
                                                                        title: 'Start Production',
                                                                        message: 'This will change the status to IN_PROGRESS. Continue?'
                                                                    })}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
                                >
                                    Start Production
                                </button>
                            )}
                            {batch.status === 'in_progress' && (
                                <button
                                    onClick={() => setConfirmDialog({
                                                                        open: true,
                                                                        action: () => handleStatusChange('completed'),
                                                                        title: 'Complete Production',
                                                                        message: 'This will mark the batch as completed. Any incomplete items will remain as is. Continue?'
                                                                    })}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none"
                                >
                                    Mark as Completed
                                </button>
                            )}
                            {(batch.status === 'planned' || batch.status === 'in_progress') && (
                                <button
                                    onClick={() => setConfirmDialog({
                                                                        open: true,
                                                                        action: () => handleStatusChange('cancelled'),
                                                                        title: 'Cancel Production',
                                                                        message: 'This will cancel the production batch. Continue?'
                                                                    })}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none"
                                >
                                    Cancel Production
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'items' && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Batch Items</h3>
                            <button
                                onClick={() => navigate(`/production/batch-item/create/${batch.id}`)}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none"
                            >
                                Add Item
                            </button>
                        </div>

                        {batch.items && batch.items.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variant</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {batch.items.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.product.sku} - {item.product.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.variant || 'Default'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.quantity}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {renderStatusBadge(item.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {batch.status !== 'completed' && (
                                                    <>
                                                        <button
                                                            onClick={() => navigate(`/production/${batch.id}`)}
                                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                                        >
                                                            View
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`/production/${batch.id}/edit`)}
                                                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                                                        >
                                                            Edit
                                                        </button>
                                                        {item.status !== 'cancelled' && (
                                                            <button
                                                                onClick={() => setStepAction({
                                                                                                 step: item,
                                                                                                 quantity: item.quantity,
                                                                                                 reason: '',
                                                                                                 open: true
                                                                                             })}
                                                                className="text-green-600 hover:text-green-900"
                                                            >
                                                                Complete
                                                            </button>
                                                        )}
                                                    </>
                                                )}

                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                             <div className="text-center py-8 text-gray-500">
                                 No items defined for this production batch
                             </div>
                         )}
                    </div>
                )}

                {activeTab === 'materials' && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Required Materials</h3>
                            {batch.status === 'planned' && (
                                <button
                                    onClick={() => setConfirmDialog({
                                                                        open: true,
                                                                        action: handleAllocateMaterials,
                                                                        title: 'Allocate Materials',
                                                                        message: 'This will allocate all required materials from inventory and start production. Continue?'
                                                                    })}
                                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none"
                                >
                                    Allocate Materials
                                </button>
                            )}
                        </div>

                        {batch.materials && batch.materials.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required Qty</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allocated</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {batch.materials.map((material) => (
                                        <tr key={material.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {material.part.sku}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {material.part.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {material.required_quantity}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {material.allocated_quantity || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {material.allocated_quantity >= material.required_quantity ? (
                                                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                                        Allocated
                                                    </span>
                                                ) : material.allocated_quantity > 0 ? (
                                                    <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                                                        Partial
                                                    </span>
                                                ) : (
                                                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                                        Pending
                                                    </span>
                                                    )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                             <div className="text-center py-8 text-gray-500">
                                 No materials defined for this production batch
                             </div>
                         )}
                    </div>
                )}

                {activeTab === 'issues' && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Issues & Defects</h3>
                            <button
                                onClick={() => navigate(`/production/issue/create/${batch.id}`)}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none"
                            >
                                Report Issue
                            </button>
                        </div>

                        {batch.issues && batch.issues.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Related Item</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {batch.issues.map((issue) => (
                                        <tr key={issue.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {format(new Date(issue.created_at), 'MMM d, yyyy')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {issue.type}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {issue.description}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {issue.batch_item ? `${issue.batch_item.product.sku}` : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    issue.status === 'resolved'
                                                    ? 'bg-green-100 text-green-800'
                                                    : issue.status === 'in_progress'
                                                      ? 'bg-yellow-100 text-yellow-800'
                                                      : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {issue.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => navigate(`/production/issue/${issue.id}`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                             <div className="text-center py-8 text-gray-500">
                                 No issues reported for this production batch
                             </div>
                         )}
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {confirmDialog.open && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">{confirmDialog.title}</h3>
                        <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setConfirmDialog({ open: false, action: null, message: '', title: '' })}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDialog.action}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step Action Dialog */}
            {stepAction.open && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Complete Item</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantity to Complete
                            </label>
                            <input
                                type="number"
                                min="1"
                                max={stepAction.step?.quantity || 1}
                                value={stepAction.quantity}
                                onChange={(e) => setStepAction(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes (Optional)
                            </label>
                            <textarea
                                value={stepAction.reason}
                                onChange={(e) => setStepAction(prev => ({ ...prev, reason: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows="3"
                            ></textarea>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setStepAction(prev => ({ ...prev, open: false }))}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCompleteStep}
                                disabled={stepAction.quantity <= 0}
                                className={`px-4 py-2 rounded-md focus:outline-none ${
                                    stepAction.quantity > 0
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                Complete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionBatchDetails;
